import React, { useState } from 'react';
import { KTSVG } from '../../../../_metronic/helpers';
import { toast } from 'react-toastify';
import { signOffCrew } from '../core/_requests';
import { showErrorToast } from '../../../shared/utils/apiErrorMessage';

/** format “YYYY-MM-DD” (or ISO) → “DD-MMM-YYYY” e.g. “12-Jul-2025” */
// allow string|null|undefined
const formatDate = (iso?: string | null) => {
  // 1) no value
  if (!iso) {
    return '—'
  }
  const d = new Date(iso)
  // 2) invalid date
  if (isNaN(d.getTime())) {
    return '—'
  }
  const dd = String(d.getDate()).padStart(2, '0')
  const mmm = d.toLocaleString('en-US', { month: 'short' })
  const yyyy = d.getFullYear()
  return `${dd}-${mmm}-${yyyy}`
}


interface Props {
  isOpen: boolean;
  onClose: () => void;
  recordId: number;
  signOnDate: string;             
  onSignedOff: (updated: {
    id: number;
    signOffDate: string;
    signOffReason: string;
    portSignOff: string;
    status: string;
  }) => void;
}

const SignOffModal: React.FC<Props> = ({
  isOpen, onClose, recordId, signOnDate, onSignedOff
}) => {
  const [portSignOff, setPortSignOff]       = useState('');
  const [signOffDate, setSignOffDate]       = useState('');
  const [signOffReason, setSignOffReason]   = useState('');
  const [isSubmitting, setIsSubmitting]     = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!portSignOff?.trim()) {
      toast.warn('Please enter port of sign-off.');
      return;
    }
    if (!signOffDate) {
      toast.warn('Please select sign-off date.');
      return;
    }
    if (!signOffReason?.trim()) {
      toast.warn('Please enter reason for sign-off.');
      return;
    }

    const so = new Date(signOnDate);
    const sf = new Date(signOffDate);
    if (sf < so) {
      toast.warn(`Sign-off date cannot be before sign-on date (${formatDate(signOnDate)}).`);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = { recordId, signOffDate, signOffReason: signOffReason.trim(), portSignOff: portSignOff.trim() };
      const resp = await signOffCrew(payload);
      toast.success('Crew signed off successfully');
      onSignedOff({
        id: resp.id,
        signOffDate: resp.signOffDate!,
        signOffReason: resp.signOffReason!,
        portSignOff: resp.portSignOff!,
        status: resp.status
      });
      onClose();
    } catch (err) {
      console.error('Sign off failed:', err);
      showErrorToast(err, 'Failed to sign off crew.', 'update');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={e=>e.stopPropagation()} style={{ maxWidth:'40rem' }}>
        <div className="custom-modal-header d-flex justify-content-between align-items-center">
          <h5>Sign Off Crew</h5>
          <button className="close-btn" onClick={onClose} disabled={isSubmitting}>
            <KTSVG path="/media/map/x.svg" className="svg-icon-2x" />
          </button>
        </div>
        <div className="custom-modal-body">
          <div className="row gy-3">
            <div className="col-md-6">
              <label className="modal_label">Port Sign-Off <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                value={portSignOff}
                onChange={e => setPortSignOff(e.target.value)}
                placeholder="Enter port of sign-off"
                required
              />
            </div>
            <div className="col-md-6">
              <label className="modal_label">Sign-Off Date <span className="text-danger">*</span></label>
              <input
                type="date"
                className="form-control"
                value={signOffDate}
                onChange={e => setSignOffDate(e.target.value)}
                required
              />
            </div>
            <div className="col-12">
              <label className="modal_label">Reason <span className="text-danger">*</span></label>
              <textarea
                className="form-control"
                rows={3}
                value={signOffReason}
                onChange={e => setSignOffReason(e.target.value)}
                placeholder="Enter sign-off reason..."
                required
              />
            </div>
          </div>
        </div>
        <div className="d-flex justify-content-end gap-2 p-3">
          <button className="btn btn_secondary" onClick={onClose} disabled={isSubmitting}>Cancel</button>
          <button className="btn btn_success" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Confirming...
              </>
            ) : (
              'Confirm'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignOffModal;